import * as React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton as MuiIconButton,
    Box,
    Button,
    Stack,
    Typography,
    TextField,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { Switch } from '../switch/Switch.tsx';

export interface VariantItem {
    name: string;
    size: string;
    price: string;
    posId: string;
}

export interface VariantGroup {
    key: string;
    name: string;
    isRequired: boolean;
    isSingleChoice: boolean;
    items: VariantItem[];
}

interface ItemErrors {
    name?: string;
    price?: string;
}

interface GroupErrors {
    name?: string;
    items?: string;
    rows?: Record<number, ItemErrors>;
}

const emptyItem = (): VariantItem => ({ name: '', size: '', price: '', posId: '' });

// A row the user has actually started filling in. Fully-empty trailing rows are ignored.
const isMeaningful = (i: VariantItem): boolean =>
    Boolean(i.name.trim() || i.price.trim() || i.size.trim() || i.posId.trim());

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
    },
    '& .MuiInputBase-input': { fontSize: 14, padding: '10px 12px' },
};

type Props = {
    open: boolean;
    onClose: () => void;
    onSave: (group: VariantGroup) => void;
    initialGroup?: VariantGroup | null;
    makeKey: () => string;
};

export const VariantGroupDialog: React.FC<Props> = ({ open, onClose, onSave, initialGroup, makeKey }) => {
    const { t } = useTranslation();

    const [name, setName] = React.useState('');
    const [isSingleChoice, setIsSingleChoice] = React.useState(true);
    const [isRequired, setIsRequired] = React.useState(false);
    const [items, setItems] = React.useState<VariantItem[]>([emptyItem()]);
    const [errors, setErrors] = React.useState<GroupErrors>({});

    // Seed the form whenever the dialog opens (either fresh or from an existing group).
    React.useEffect(() => {
        if (!open) return;
        setName(initialGroup?.name ?? '');
        setIsSingleChoice(initialGroup?.isSingleChoice ?? true);
        setIsRequired(initialGroup?.isRequired ?? false);
        setItems(
            initialGroup?.items?.length
                ? initialGroup.items.map(i => ({ ...i }))
                : [emptyItem()]
        );
        setErrors({});
    }, [open, initialGroup]);

    const updateItem = (index: number, field: keyof VariantItem, value: string) => {
        setItems(prev => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
    };

    const addItemRow = () => setItems(prev => [...prev, emptyItem()]);

    const removeItemRow = (index: number) => {
        setItems(prev => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
    };

    const validate = (): GroupErrors => {
        const next: GroupErrors = {};
        if (!name.trim()) next.name = t('locationsManagement.createItem.groupNameRequiredError');

        if (items.filter(isMeaningful).length === 0) {
            next.items = t('locationsManagement.createItem.atLeastOneItemError');
        }

        const rows: Record<number, ItemErrors> = {};
        items.forEach((it, i) => {
            const rowErr: ItemErrors = {};
            if (!isMeaningful(it)) return; // fully-empty trailing rows are ignored, not errored
            if (!it.name.trim()) rowErr.name = t('locationsManagement.createItem.itemNameRequiredError');
            const priceNum = Number(it.price);
            if (it.price.trim() === '' || Number.isNaN(priceNum) || priceNum < 0) {
                rowErr.price = t('locationsManagement.createItem.itemPriceRequiredError');
            }
            if (Object.keys(rowErr).length) rows[i] = rowErr;
        });
        if (Object.keys(rows).length) next.rows = rows;
        return next;
    };

    const handleSave = () => {
        const found = validate();
        setErrors(found);
        if (Object.keys(found).length) return;

        const cleanedItems = items
            .filter(isMeaningful)
            .map(i => ({
                name: i.name.trim(),
                size: i.size.trim(),
                price: i.price.trim(),
                posId: i.posId.trim(),
            }));

        onSave({
            key: initialGroup?.key ?? makeKey(),
            name: name.trim(),
            isRequired,
            isSingleChoice,
            items: cleanedItems,
        });
        onClose();
    };

    const isEdit = Boolean(initialGroup);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            slotProps={{
                backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
                paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isEdit
                    ? t('locationsManagement.createItem.editVariantGroup')
                    : t('locationsManagement.createItem.addVariantGroup')}
                <MuiIconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </MuiIconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Group-level settings */}
                <Stack spacing={2.5}>
                    <Box>
                        <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', mb: 0.5 }}>
                            {t('locationsManagement.createItem.groupName')}
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            sx={inputSx}
                            placeholder={t('locationsManagement.createItem.groupNamePlaceholder')}
                            value={name}
                            onChange={e => setName(e.target.value)}
                            error={Boolean(errors.name)}
                            helperText={errors.name || ' '}
                            slotProps={{ htmlInput: { maxLength: 100 } }}
                        />
                    </Box>

                    <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap">
                        <FormControl>
                            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', mb: 0.5 }}>
                                {t('locationsManagement.createItem.choiceType')}
                            </Typography>
                            <RadioGroup
                                row
                                value={isSingleChoice ? 'single' : 'multiple'}
                                onChange={e => setIsSingleChoice(e.target.value === 'single')}
                            >
                                <FormControlLabel
                                    value="single"
                                    control={<Radio size="small" />}
                                    label={t('locationsManagement.createItem.singleChoice')}
                                />
                                <FormControlLabel
                                    value="multiple"
                                    control={<Radio size="small" />}
                                    label={t('locationsManagement.createItem.multipleChoice')}
                                />
                            </RadioGroup>
                        </FormControl>

                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                                {t('locationsManagement.createItem.required')}
                            </Typography>
                            <Switch
                                checked={isRequired}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsRequired(e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        </Stack>
                    </Stack>

                    {/* Items */}
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'text.secondary', mb: 1 }}>
                            {t('locationsManagement.createItem.items')}
                        </Typography>

                        <Stack spacing={1}>
                            {items.map((it, index) => {
                                const rowErr = errors.rows?.[index];
                                return (
                                    <Stack
                                        key={index}
                                        direction="row"
                                        spacing={1}
                                        alignItems="flex-start"
                                    >
                                        <TextField
                                            size="small"
                                            sx={{ ...inputSx, flex: 2 }}
                                            placeholder={t('locationsManagement.createItem.name')}
                                            value={it.name}
                                            onChange={e => updateItem(index, 'name', e.target.value)}
                                            error={Boolean(rowErr?.name)}
                                            helperText={rowErr?.name || ' '}
                                            slotProps={{ htmlInput: { maxLength: 400 } }}
                                        />
                                        <TextField
                                            size="small"
                                            sx={{ ...inputSx, flex: 1 }}
                                            placeholder={t('locationsManagement.createItem.amount')}
                                            value={it.size}
                                            onChange={e => updateItem(index, 'size', e.target.value)}
                                            helperText=" "
                                            slotProps={{ htmlInput: { maxLength: 400 } }}
                                        />
                                        <TextField
                                            size="small"
                                            sx={{ ...inputSx, flex: 1 }}
                                            placeholder={t('locationsManagement.createItem.price')}
                                            value={it.price}
                                            onChange={e => updateItem(index, 'price', e.target.value)}
                                            error={Boolean(rowErr?.price)}
                                            helperText={rowErr?.price || ' '}
                                            slotProps={{ htmlInput: { maxLength: 400 } }}
                                        />
                                        <TextField
                                            size="small"
                                            sx={{ ...inputSx, flex: 1 }}
                                            placeholder={t('locationsManagement.createItem.posId')}
                                            value={it.posId}
                                            onChange={e => updateItem(index, 'posId', e.target.value)}
                                            helperText=" "
                                            slotProps={{ htmlInput: { maxLength: 400 } }}
                                        />
                                        <MuiIconButton
                                            aria-label="remove item"
                                            onClick={() => removeItemRow(index)}
                                            disabled={items.length === 1}
                                            sx={{ mt: 0.5 }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" sx={{ color: 'var(--color-error)' }} />
                                        </MuiIconButton>
                                    </Stack>
                                );
                            })}
                        </Stack>

                        {errors.items && (
                            <FormHelperText error>{errors.items}</FormHelperText>
                        )}

                        <Button
                            type="button"
                            startIcon={<AddIcon />}
                            onClick={addItemRow}
                            sx={{ mt: 1, color: 'var(--color-primary)', textTransform: 'none' }}
                        >
                            {t('locationsManagement.createItem.addItem')}
                        </Button>
                    </Box>
                </Stack>

                {/* Footer */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button
                        variant="outlined"
                        type="button"
                        onClick={onClose}
                        sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSave}
                        sx={{
                            borderRadius: 0,
                            backgroundColor: 'var(--color-primary)',
                            color: 'var(--color-text-light)',
                            boxShadow: 'none',
                            '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                        }}
                    >
                        {t('locationsManagement.createItem.saveGroup')}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
